import { Spacing, Text } from '@vkontakte/vkui';
import React, { type FC, memo, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { duplicatesTariffsModal } from 'reactApp/appHelpers/featuresHelpers/features/duplicates';
import { cdnPackagesUrl } from 'reactApp/appHelpers/themeToolHelpers/default-values';
import { WhatsNewDialog } from 'reactApp/components/WhatsNewDialog/WhatsNewDialog';
import { useDuplicatesAnalytic } from 'reactApp/hooks/duplicates/useDuplicatesAnalytic';
import { useTariffBuy } from 'reactApp/modules/payment/hooks/useTariffBuy';
import { openBuyDisableAds } from 'reactApp/modules/payment/payment.module';
import { isCurrentDarkTheme } from 'reactApp/modules/theme/theme.selectors';
import { sendXray } from 'reactApp/utils/ga';
import { ECategoryGa, sendPaymentGa } from 'reactApp/utils/paymentGa';

import styles from './DuplicatesMailSpacePromoModal.css';

interface Props {
    onClose?: () => void;
}

const name = 'dp-promo-modal';

const handleDismiss = () => {
    sendXray([name, 'close']);
};

export const DuplicatesMailSpacePromoModal: FC<Props> = memo(function DuplicatesMailSpacePromoModal({ onClose }: Props) {
    const isDarkTheme = useSelector(isCurrentDarkTheme);
    const analyticData = useDuplicatesAnalytic();

    const dispatch = useDispatch();

    const onClickBuy = useTariffBuy({
        paySource: duplicatesTariffsModal.paySource,
    });

    const handleClose = useCallback(() => {
        onClose?.();
    }, [onClose]);

    const handleShow = useCallback(() => {
        sendXray([name, 'show']);

        sendPaymentGa({
            eventCategory: ECategoryGa.duplicates,
            action: 'modal-show',
            modal_type: 'promo',
            ...analyticData,
        });
    }, [analyticData]);

    const handleClick = useCallback(() => {
        sendXray([name, 'click']);

        sendPaymentGa({
            eventCategory: ECategoryGa.duplicates,
            action: 'modal-click',
            modal_type: 'promo',
            action_type: 'try-promo',
            ...analyticData,
        });

        handleClose();
        dispatch(
            openBuyDisableAds({
                title: duplicatesTariffsModal.buyTitle,
                tariffsNumber: duplicatesTariffsModal.tariffsNumber,
                activeTab: duplicatesTariffsModal.activeTab,
                isFrame: false,
                onBuyClick: onClickBuy,
                actionShow: duplicatesTariffsModal.actionShow,
                actionClick: duplicatesTariffsModal.actionClick,
            })
        );
    }, [analyticData, dispatch, handleClose, onClickBuy]);

    return (
        <WhatsNewDialog
            imageUrl={`${cdnPackagesUrl}/static/cloud/promo/duplicates/onboarding-modal${isDarkTheme ? '-dark' : '-light'}.png`}
            imageWrapperClassName={styles.imageWrapper}
            contentClassName={styles.content}
            title="Можно удалить все дубликаты с подпиской Mail Space"
            content={
                <>
                    <Spacing size={16} />
                    <Text data-qa-id="text">
                        Вам&nbsp;не&nbsp;придётся удалять похожие фото по&nbsp;одному&nbsp;— в&nbsp;корзину переместятся сразу все,
                        и&nbsp;место в&nbsp;Облаке освободится
                    </Text>
                    <Spacing size={32} />
                </>
            }
            primaryButtonText="Попробовать подписку"
            closeAfterClick
            onClick={handleClick}
            onShow={handleShow}
            onDismiss={handleDismiss}
            onClose={handleClose}
            imageBgColor="var(--vkui--color_background_modal)"
            buttonTheme="vk"
            dialogSize="small"
            qaId="duplicates-mail-space-promo-modal"
            isRebranding
        />
    );
});

DuplicatesMailSpacePromoModal.displayName = 'DuplicatesMailSpacePromoModal';
